import * as moment from "moment";
import {DeviceAvdbSeries} from "../device-avdb-series";
import {DeviceAvdbType, DeviceAvdbTypeStatus} from "../device-avdb-type";
import {DeviceAvdbInstalledIssue} from "../device-avdb-installed-issue";

export class AvdbTypeRowStatus {
	readonly showJeppesenLogin: boolean;

	// Set by methods in this class
	statusColor: string;
	statusIcon: string;
	statusText: string;
	statusSubtext: string;
	issuesRemaining: string;
	expectedEndDate: moment.Moment;
	hasCheckIcon: boolean;
	hasExclamationIcon: boolean;
	isIgnoreButtonVisible: boolean = false;
	isIssueVisible: Boolean = true;
	installedSeries: DeviceAvdbSeries = null;

	private readonly avdbType: DeviceAvdbType;

	constructor(avdbType: DeviceAvdbType, installedIssue: DeviceAvdbInstalledIssue = null, avdbStatus: string | DeviceAvdbTypeStatus = null) {
		this.avdbType = avdbType;
		if (installedIssue) {
			this.setStatusForInstalledIssue(installedIssue);
		} else if (avdbStatus) {
			this.setStatus(avdbStatus);
		} else if (this.avdbType.installedIssue) {
			this.setStatusForInstalledIssue(this.avdbType.installedIssue);
		} else {
			this.setStatus();
		}
	}

	setStatus(avdbStatus: string = this.avdbType.status, installedSeries: DeviceAvdbSeries = this.getSeries(this.avdbType.installedIssue), installedIssue: DeviceAvdbInstalledIssue = this.avdbType.installedIssue, statusForIssue: boolean = false) {
		switch (avdbStatus) {
			case DeviceAvdbTypeStatus.NOT_INSTALLED:
				this.statusColor = "statuscolor-gray";
				this.statusIcon = "fa fa-minus-circle";
				this.statusText = "Not installed";
				break;
			case DeviceAvdbTypeStatus.LATEST:
				this.statusColor = "statuscolor-green";
				this.statusIcon = "fa fa-check-circle";
				this.hasCheckIcon = true;
				this.hasExclamationIcon = false;
				let installedAt = moment(installedIssue.installedAt).format('DD-MMM-YYYY h:mm:ss a');
				this.statusText = `${statusForIssue === true ? 'Database' : 'Database(s)'} Updated On`;
				this.statusSubtext = `${installedAt}`;
				this.issuesRemaining = `${installedSeries.issuesRemaining} ${installedSeries.issuesRemaining != 1 ? 'updates' : 'update'} remaining`;
				this.expectedEndDate = installedSeries.expectedEndDate;
				break;
			case DeviceAvdbTypeStatus.OUTDATED:
				this.statusColor = "statuscolor-orange";
				this.statusIcon = "fa fa-exclamation-triangle";
				this.issuesRemaining = `${installedSeries.issuesRemaining} ${installedSeries.issuesRemaining != 1 ? 'updates' : 'update'} remaining`;
				this.expectedEndDate = installedSeries.expectedEndDate;
				this.hasCheckIcon = false;
				this.hasExclamationIcon = true;
				this.statusSubtext = "";
				if (statusForIssue === false) {
					this.statusText = "Expires " + this.getDurationFromInvalidAt(this.avdbType.installedIssue.invalidAt);
				} else {
					this.statusText = "Expires " + this.getDurationFromInvalidAt(installedIssue.invalidAt);
				}
				break;
			case DeviceAvdbTypeStatus.EXPIRED:
				this.statusColor = "statuscolor-red";
				this.statusIcon = "fa fa-exclamation-circle";
				this.issuesRemaining = `${installedSeries.issuesRemaining} ${installedSeries.issuesRemaining != 1 ? 'updates' : 'update'} remaining`;
				this.expectedEndDate = installedSeries.expectedEndDate;
				this.hasCheckIcon = false;
				this.hasExclamationIcon = true;
				this.statusSubtext = "";
				if (statusForIssue === false) {
					this.statusText = `Expired ${this.getDurationFromInvalidAt(this.avdbType.installedIssue.invalidAt)}`;
				} else {
					this.statusText = `Expired ${this.getDurationFromInvalidAt(installedIssue.invalidAt)}`;
				}
				break;
			default:
				this.statusColor = "statuscolor-gray";
				this.statusIcon = "fa fa-minus-circle";
				this.statusText = "Unknown status";
				this.hasCheckIcon = false;
				this.hasExclamationIcon = false;
		}

		if (this.showJeppesenLogin) {
			this.statusColor = "statuscolor-red";
			this.statusIcon = "fa fa-exclamation-circle";
			this.statusText = "Login Required";
			this.hasCheckIcon = false;
			this.hasExclamationIcon = true;
		}

		if (installedSeries && installedSeries.isFree) {
			this.issuesRemaining = "";
			this.expectedEndDate = undefined;
		}

		return true;
	}

	setStatusForInstalledIssue(installedIssue: DeviceAvdbInstalledIssue) {
		if (installedIssue) {
			this.installedSeries = this.getSeries(installedIssue);

			let installedIssueStatus = DeviceAvdbTypeStatus.OUTDATED;
			if (installedIssue.isExpired) {
				installedIssueStatus = DeviceAvdbTypeStatus.EXPIRED;
			} else if (this.installedSeries && this.installedSeries.availableIssues.length > 0 && this.installedSeries.availableIssues.reduce((prev, current) => {
				return prev.effectiveAt > current.effectiveAt ? prev : current;
			}).name === installedIssue.name) {
				installedIssueStatus = DeviceAvdbTypeStatus.LATEST;
			}

			if (this.installedSeries) {
				this.setStatus(installedIssueStatus, this.installedSeries, installedIssue, true);
			} else {
				this.setStatus();
			}

			this.isIgnoreButtonVisible = false;
			if (installedIssue.isExpired && this.installedSeries && this.installedSeries.issuesRemaining == 0 && this.installedSeries.installableIssues.length == 0) {
				this.isIgnoreButtonVisible = true;
			}

			this.isIssueVisible = true;
			if (!installedIssue.critical && this.isIgnoreButtonVisible) {
				this.isIssueVisible = false;
				this.isIgnoreButtonVisible = false;
			}
		}
		return true;
	}

	private getSeries(installedIssue: DeviceAvdbInstalledIssue) {
		return installedIssue ? this.avdbType.series.find(series => series.id == installedIssue.seriesID) : null;
	}

	private getDurationFromInvalidAt(invalidAt: moment.Moment) {
		if (invalidAt) {
			return invalidAt.fromNow();
		} else {
			return null;
		}
	}
}
